import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { createRef, ReactNode, useCallback, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    maxNumberOfColumns: number;
    slides: CarouselSlide[];
    onRemoveAll: () => void;
    onOpenCompare: () => void;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    mainContainer?: GridContainerProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    removeButtonSection?: InjectableCss;
    compareButtonSection?: InjectableCss;
    headerRemoveAllButton?: ButtonPresentationProps;
    headerCompareButton?: ButtonPresentationProps;
    emptySlide?: InjectableCss;
    hideEmptySlide?: InjectableCss;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: inherit;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            width: auto !important;
            margin-right: 13px;
            @media all and (max-width: 540px) and (min-width: 0px) {
                position: inherit;
                margin-right: 5px;
            }
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 5px;
            display: flex;
            flex-direction: column;
            height: 90px;
            button[data-test-selector="productCompareRemoveProductButton"] {
                background: none !important;
                padding: 0;
                width: 30px;
                height: 30px;
                border: none !important;
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                position: absolute;
                top: -5px;
                left: 4px;
                span:nth-child(2) {
                    top: 0;
                }
            }
            a[data-test-selector="productImage"] {
                width: 100%;
            }
            img {
                width: 65px;
            }
        `,
    },
    removeButtonSection: {
        css: css`
            position: absolute;
            right: 0;
            bottom: 0;
            @media all and (max-width: 540px) and (min-width: 0px) {
                position: inherit;
                margin-bottom: 8px;
            }
        `,
    },
    headerRemoveAllButton: {
        css: css`
            box-sizing: border-box;
            height: 41px;
            width: 209px;
            border: 1px solid #cccccc;
            background-color: #ffffff;
            > span {
                color: #33333a;
                font-size: 12px;
                font-weight: 600;
                letter-spacing: 2px;
                line-height: 20px;
                text-align: center;
                text-transform: uppercase;
            }
            &:hover {
                background: #fff;
            }
            @media all and (max-width: 540px) and (min-width: 0px) {
                width: 90px;
                letter-spacing: 1px;
                padding: 0;
                min-height: auto;
                height: 34px;
            }
            @media all and (max-width: 720px) and (min-width: 540px) {
                width: 109px;
                letter-spacing: 1px;
                padding: 0;
            }
        `,
    },

    compareButtonSection: {
        css: css`
            position: absolute;
            right: 0;
            top: 0;
            @media all and (max-width: 540px) and (min-width: 0px) {
                position: inherit;
                margin-bottom: 8px;
            }
        `,
    },

    headerCompareButton: {
        css: css`
            height: auto;
            width: 208px;
            > span {
                display: inline-block;
                font-size: 12px;
                font-weight: bold;
                letter-spacing: 2px;
                padding: 8px 0px;
                text-align: center;
                text-transform: uppercase;
            }
            @media all and (max-width: 540px) and (min-width: 0px) {
                width: 90px;
                letter-spacing: 1px;
                padding: 0;
                min-height: auto;
                height: auto;
            }
            @media all and (max-width: 720px) and (min-width: 540px) {
                width: 108px;
                letter-spacing: 1px;
                padding: 0;
            }
        `,
    },

    emptySlide: {
        css: css`
            padding: 0;
            // position: relative;
            flex: 0 0 74px;
            // width: 100px !important;
            margin-right: 13px !important;
            border: 1px solid #e0e0e0;
            height: 80px;
            margin: auto 0;
            min-height: 80px;
            // min-width: 100px;
            @media all and (max-width: 540px) and (min-width: 0px) {
                position: inherit;
                margin-right: 5px !important;
                margin-top: 4px !important;
            }
        `,
    },
};

const Carousel = ({ theme, extendedStyles, title, maxNumberOfColumns, slides, onRemoveAll, onOpenCompare }: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }
        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());
    return (
        <>
            <Typography {...styles.titleText}>{title}</Typography>
            <GridContainer {...styles.mainContainer} data-test-selector="carousel">
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {slides.map((slide, index) => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={slide.id}
                                        style={{ flex: `0 0 74px` }}
                                        tabIndex={index + 1}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            data-test-key={slide.id}
                                            id={`slide_${slide.id}`}
                                            data-test-selector="carousel_slide"
                                        >
                                            {slide.renderComponent({})}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                                {!(slides.length > 1) && <StyledWrapper {...styles.emptySlide}></StyledWrapper>}
                                {!(slides.length > 2) && <StyledWrapper {...styles.emptySlide}></StyledWrapper>}
                                <Hidden below="md">
                                    <StyledWrapper {...styles.compareButtonSection}>
                                        <Button
                                            {...styles.headerCompareButton}
                                            onClick={onOpenCompare}
                                            data-test-selector="productCompareOpenComparePageButton"
                                        >
                                            {translate("Compare Products")}
                                        </Button>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.removeButtonSection}>
                                        <Button
                                            {...styles.headerRemoveAllButton}
                                            onClick={onRemoveAll}
                                            data-test-selector="productCompareRemoveAllButton"
                                        >
                                            {translate("Clear All")}
                                        </Button>
                                    </StyledWrapper>
                                </Hidden>
                                <Hidden
                                    above="md"
                                    css={css`
                                        @media all and (max-width: 540px) and (min-width: 0px) {
                                            margin-left: 8px;
                                            margin-top: 5px;
                                        }
                                    `}
                                >
                                    <StyledWrapper {...styles.compareButtonSection}>
                                        <Button
                                            {...styles.headerCompareButton}
                                            onClick={onOpenCompare}
                                            data-test-selector="productCompareOpenComparePageButton"
                                        >
                                            {translate("Compare")}
                                        </Button>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.removeButtonSection}>
                                        <Button
                                            {...styles.headerRemoveAllButton}
                                            onClick={onRemoveAll}
                                            data-test-selector="productCompareRemoveAllButton"
                                        >
                                            {translate("Clear")}
                                        </Button>
                                    </StyledWrapper>
                                </Hidden>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default withIsInShell(withTheme(Carousel));
